import {ProjectFeatureFlag} from 'common/report/constants';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {
  PRESET_DATE_RANGE_DISPLAY_NAMES,
  PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS,
  PresetDateRange,
} from 'common/widgets/date-range-picker/models/presets';

import {AutoDetectedExperimentSource} from '../util/report';

// percentage points; (stopgap for padding/flex-grow issues, see getTreatmentTableColumns comments)
export const METRIC_NAME_WIDTH_PERCENT = 30;
export const CONTROL_WIDTH_PERCENT = 15;

export const DEFAULT_TIME_FILTER = {
  displayText: PRESET_DATE_RANGE_DISPLAY_NAMES[PresetDateRange.Last30Days],
  dateRange: new DateRange(PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.Last30Days]),
};

export const LAST_1_DAY_TIME_FILTER = {
  displayText: PRESET_DATE_RANGE_DISPLAY_NAMES[PresetDateRange.Last1Day],
  dateRange: new DateRange(PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.Last1Day]),
};

export const LAST_7_DAYS_TIME_FILTER = {
  displayText: PRESET_DATE_RANGE_DISPLAY_NAMES[PresetDateRange.Last7Days],
  dateRange: new DateRange(PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.Last7Days]),
};

export const SHOW_LAST_X_DAYS_FILTER = {
  [ProjectFeatureFlag.ShowLast1Day]: LAST_1_DAY_TIME_FILTER,
  [ProjectFeatureFlag.ShowLast7Days]: LAST_7_DAYS_TIME_FILTER,
};

export enum AutoDetectExperimentKeys {
  EXPERIMENT_EVENT_NAME = `experiment_event_name`,
  EXPERIMENT_PROPERTY_NAME = `experiment_property_name`,
  VARIANT_PROPERTY_NAME = `variant_property_name`,
  SOURCE_PROPERTY_NAME = `source_property_name`,
  SUPPORTED_SOURCES = `supported_sources`,
}

export const EXPERIMENT_STARTED_EVENT = `$experiment_started`;
export const EXPERIMENT_NAME_PROPERTY = `Experiment name`;
export const EXPERIMENT_VARIANT_PROPERTY = `Variant name`;
export const EXPERIMENT_SOURCE_PROPERTY = `Source`;

const DEFAULT_EXPERIMENT_VALUES = {
  [AutoDetectExperimentKeys.EXPERIMENT_EVENT_NAME]: EXPERIMENT_STARTED_EVENT,
  [AutoDetectExperimentKeys.EXPERIMENT_PROPERTY_NAME]: EXPERIMENT_NAME_PROPERTY,
  [AutoDetectExperimentKeys.VARIANT_PROPERTY_NAME]: EXPERIMENT_VARIANT_PROPERTY,
  [AutoDetectExperimentKeys.SOURCE_PROPERTY_NAME]: EXPERIMENT_SOURCE_PROPERTY,
  [AutoDetectExperimentKeys.SUPPORTED_SOURCES]: [AutoDetectedExperimentSource.Chameleon] as string[],
};
type ExperimentValues = typeof DEFAULT_EXPERIMENT_VALUES;

// TODO let users set this in appdb or the UI or something
const PROJECT_EXPERIMENT_OVERRIDES: Record<number, ExperimentValues> = {
  2087667: {
    // Uber production
    [AutoDetectExperimentKeys.EXPERIMENT_EVENT_NAME]: `experiment.exposure.gxp_next_xp`,
    [AutoDetectExperimentKeys.EXPERIMENT_PROPERTY_NAME]: `experiment_key`,
    [AutoDetectExperimentKeys.VARIANT_PROPERTY_NAME]: `treatment_group`,
    [AutoDetectExperimentKeys.SOURCE_PROPERTY_NAME]: `experiment_source`,
    [AutoDetectExperimentKeys.SUPPORTED_SOURCES]: [`EXPERIMENT_SOURCE_CITRUS`, `EXPERIMENT_SOURCE_SAPPHIRE`],
  },
};

/** Gets the experiment keys, handling per-project overrides. */
export function getExperimentKey<T extends AutoDetectExperimentKeys>(key: T, projectId: number): ExperimentValues[T] {
  return PROJECT_EXPERIMENT_OVERRIDES[projectId]?.[key] || DEFAULT_EXPERIMENT_VALUES[key];
}
